import React, { useState } from 'react';
import { Layout, Menu, Dropdown, Avatar, Tag, Space, Grid, Drawer, Button, theme } from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  TeamOutlined,
  MenuOutlined,
  AuditOutlined,
  CalendarOutlined,
  DashboardOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import Footer from './Footer';
import ThemeControl from '../Common/ThemeControl';

import logoMenu from '../../assets/6.png';

const { Header, Content, Sider } = Layout;
const { useBreakpoint } = Grid;

/**
 * Panel del fiscal-admin.
 *
 * Comparte los PATRONES del panel de usuario —tokens de tema, menú lateral en
 * escritorio y cajón en móvil, logo, selector de tema y pie— para que el gestor
 * no perciba dos productos distintos. No comparte el componente: el menú, los
 * créditos y el guard son distintos por diseño.
 *
 * En particular sigue usando rutas fijas y guard propio, y NO el mecanismo
 * client_access → screens del panel de usuario: ese es exclusivo del cliente y
 * además es fail-open (concede cuando allowed_screens viene ausente).
 *
 * El chrome se pinta con tokens del tema, nunca con colores fijos: antes el
 * header iba en un azul de la paleta con textos en blanco, y en tema claro
 * quedaba una franja oscura sobre fondo claro.
 */
const FiscalLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const { token } = theme.useToken();
  const [drawerVisible, setDrawerVisible] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const ir = (ruta) => {
    navigate(ruta);
    setDrawerVisible(false);
  };

  // "Hoy" primero: es la pantalla de vigilancia, la que responde "cómo va el mes"
  // de un vistazo desde el celular. La agenda es el trabajo ("qué me falta") y la
  // cartera responde otra pregunta distinta ("a quién atiendo"). Desde las tres se
  // abre la gestión fiscal sobre el contribuyente elegido.
  const menuItems = [
    {
      key: '/fiscal/hoy',
      icon: <DashboardOutlined />,
      label: 'Hoy',
      onClick: () => ir('/fiscal/hoy'),
    },
    {
      key: '/fiscal/agenda',
      icon: <CalendarOutlined />,
      label: 'Agenda del mes',
      onClick: () => ir('/fiscal/agenda'),
    },
    {
      key: '/fiscal/cartera',
      icon: <TeamOutlined />,
      label: 'Mi cartera',
      onClick: () => ir('/fiscal/cartera'),
    },
  ];

  const userMenuItems = [
    {
      key: 'profile',
      icon: <AuditOutlined />,
      label: user?.email,
      disabled: true,
    },
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Cerrar Sesión',
      onClick: handleLogout,
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header
        style={{
          padding: screens.lg ? '0 24px' : '0 12px',
          height: screens.lg ? '64px' : '50px',
          lineHeight: screens.lg ? '64px' : '50px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: token.colorBgContainer,
          color: token.colorText,
          width: '100%',
          flexShrink: 0,
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          boxShadow: token.boxShadowTertiary,
        }}
      >
        <div
          style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', height: '100%' }}
          onClick={() => navigate('/fiscal/hoy')}
        >
          <img src={logoMenu} alt="Mi Factura" style={{ height: screens.lg ? '40px' : '28px' }} />
        </div>

        <div style={{ flex: 1 }} />

        <Space size={screens.lg ? 12 : 8}>
          {/* La etiqueta del perfil se conserva: identifica quién eres en un panel
              que un Admin también puede abrir. En móvil ocupa demasiado. */}
          {screens.md && <Tag color="cyan">fiscal-admin</Tag>}

          <ThemeControl />

          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Avatar
              style={{ backgroundColor: token.colorPrimary, cursor: 'pointer' }}
              icon={<UserOutlined />}
            />
          </Dropdown>

          {!screens.lg && (
            <Button
              type="text"
              icon={<MenuOutlined style={{ fontSize: '20px' }} />}
              onClick={() => setDrawerVisible(true)}
            />
          )}
        </Space>
      </Header>

      <Drawer
        title="Menú"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={250}
        styles={{ body: { padding: 0 } }}
      >
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          style={{ border: 'none' }}
        />
      </Drawer>

      <Layout style={{ flex: 1 }}>
        {screens.lg && (
          <Sider
            width={240}
            style={{
              backgroundColor: token.colorBgContainer,
              borderRight: `1px solid ${token.colorSplit}`,
            }}
          >
            <Menu
              mode="inline"
              selectedKeys={[location.pathname]}
              items={menuItems}
              style={{ border: 'none', height: '100%' }}
            />
          </Sider>
        )}

        <Content
          style={{
            padding: screens.lg ? '24px' : '12px',
            backgroundColor: token.colorBgLayout,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
          }}
        >
          <div style={{ flex: 1 }}>
            <Outlet />
          </div>
        </Content>
      </Layout>

      <Footer />
    </Layout>
  );
};

export default FiscalLayout;
